import type {
  AuditAction,
  AuditActor,
  AuditResult,
  AuditTarget,
} from '@ownyou/shared-types';

export interface AuditProducerEventDraft {
  id?: string;
  actor: AuditActor;
  action: AuditAction;
  target: AuditTarget;
  result?: AuditResult;
  reason?: string;
  traceId?: string;
  routeId?: string;
}

export type AuditProducerSubscriber = (
  draft: AuditProducerEventDraft,
) => void | Promise<void>;

const subscribers = new Set<AuditProducerSubscriber>();

export function subscribeAuditProducerEvents(subscriber: AuditProducerSubscriber): () => void {
  subscribers.add(subscriber);
  return () => subscribers.delete(subscriber);
}

export async function emitAuditProducerEvent(
  draft: AuditProducerEventDraft,
): Promise<{ delivered: number; failures: Error[] }> {
  if (draft.action === 'audit.event_verified') {
    throw new Error('audit.event_verified is not a product-semantic Audit action');
  }

  const failures: Error[] = [];
  let delivered = 0;
  for (const subscriber of subscribers) {
    try {
      await subscriber(draft);
      delivered += 1;
    } catch (error) {
      failures.push(error instanceof Error ? error : new Error(String(error)));
    }
  }
  if (failures.length > 0) {
    console.warn('[AuditProducer] One or more Audit event subscribers failed', {
      action: draft.action,
      failures,
    });
  }
  return { delivered, failures };
}

export async function emitRoutePreferenceAuditEvents(params: {
  previous: Record<string, unknown>;
  next: Record<string, unknown>;
}): Promise<void> {
  const changedRouteIds = new Set([
    ...Object.keys(params.previous),
    ...Object.keys(params.next),
  ]);
  const changed = [...changedRouteIds].filter((routeId) => (
    JSON.stringify(params.previous[routeId] ?? null) !== JSON.stringify(params.next[routeId] ?? null)
  ));

  if (changed.length === 0) return;

  if (changed.length > 1) {
    await emitAuditProducerEvent({
      actor: { kind: 'user' },
      action: 'settings.route_preferences_bulk_changed',
      target: {
        kind: 'settings_section',
        stableRef: 'settings:model-routing',
        label: 'Model routing',
      },
      result: 'success',
    });
    return;
  }

  const routeId = changed[0];
  const action = params.next[routeId]
    ? 'settings.route_preference_changed'
    : 'settings.route_preference_cleared';
  await emitAuditProducerEvent({
    actor: { kind: 'user' },
    action,
    target: {
      kind: 'model_route_preference',
      stableRef: `settings:model-route:${routeId}`,
      label: routeId,
    },
    result: 'success',
  });
}
